import { useQuery } from '@apollo/client';
import { GET_CONTACT_AND_CONVERSATION_BY_ID } from '@zaplify/graphql';
import React, { FC, Suspense, useMemo, useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useIsMobile } from '@shadcn/ui/hooks/use-mobile';
import { Skeleton } from '@shadcn/ui/components/ui/skeleton';
import { ConversationPageHeader } from './conversation-header';
import { useSidebars } from '@shadcn/ui/hooks/use-sidebars';
import { MessageComposerProviderV2 } from '../../hooks/messaging/message-composer/use-message-composer-v2';
import { MessageComposerV2 } from '../../pages/actions/components/message-composer/message-composer-v2';
import { SparklesAI } from '../../pages/actions/components/message-composer/sparkles-ai';
import { ContactAvatar } from '../contact-avatar';
import { ContactDetailsSidebar } from '../contact-details-sidebar/contact-details-sidebar';
import { useSnoozeDialogV2 } from '../dialogs/snooze-dialog-v2';
import { ActionMessageListSkeleton, ActionMessageList } from './message-list';

export const Conversation: FC = () => {
    const { contactId } = useParams();
    const scrollAreaRef = React.useRef<HTMLDivElement>(null);
    const { toggleSidebar, sidebarState } = useSidebars();
    const { openDialogForContact: openSnoozeDialog, SnoozeDialogV2Component } = useSnoozeDialogV2();
    const [messageJustSent, setMessageJustSent] = useState(false);

    const [messageComposerSize, setMessageComposerSize] = React.useState<'large' | 'small'>('large');
    const [lastSizeToggleTimestamp, setLastSizeToggleTimestamp] = React.useState(0);
    const isMobile = useIsMobile();

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (isMobile) {
            return;
        }
        if (Date.now() - lastSizeToggleTimestamp < 100) {
            return;
        }
        const element = e.target as HTMLDivElement;
        const isOverflowing = element.scrollHeight > element.clientHeight;
        const scrollBottom = element.scrollHeight - element.scrollTop - element.clientHeight;

        if (isOverflowing) {
            if (messageComposerSize === 'small' && scrollBottom <= 1) {
                setMessageComposerSize('large');
                scrollAreaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
                setLastSizeToggleTimestamp(Date.now());
            } else if (messageComposerSize === 'large' && scrollBottom > 60) {
                setMessageComposerSize('small');
                setLastSizeToggleTimestamp(Date.now());
            }
        }
    };

    const { data, loading, error, refetch } = useQuery(GET_CONTACT_AND_CONVERSATION_BY_ID, {
        variables: { id: contactId, messageLimit: 20, noteLimit: 20 },
        skip: !contactId,
    });

    useEffect(() => {
        setMessageJustSent(false);
    }, [contactId]);

    const contact = useMemo(() => {
        return data?.Contacts_by_pk;
    }, [data]);

    const handleMessageSent = useCallback(() => {
        setMessageJustSent(true);
        // Reset the flag after the notification appears and metadata should be updated
        setTimeout(() => {
            setMessageJustSent(false);
        }, 30000); // 30 seconds timeout to prevent stuck state
    }, [contactId]);

    const handleScheduleAction = useCallback(() => {
        openSnoozeDialog(contact?.firstName || '', contact?.id || '');
    }, [contact?.firstName, contact?.id, openSnoozeDialog]);

    if (loading) {
        return null;
    }

    if (error) {
        return <div>Error: {error.message}</div>;
    }
    return (
        <div className="flex h-full w-full max-h-full overflow-hidden">
            <div className="flex flex-col h-full w-full flex-1">
                <div className="flex flex-col h-full w-full justify-end">
                    <ConversationPageHeader
                        contact={contact}
                        onToggleDetails={() => {
                            toggleSidebar('details');
                        }}
                        isDetailsOpen={sidebarState.details}
                    />
                    <MessageComposerProviderV2
                        key={contactId}
                        contactId={contact?.id}
                        onMessageSent={handleMessageSent}
                    >
                        <Suspense fallback={<ActionMessageListSkeleton />}>
                            {contact?.Messages?.length > 0 ? (
                                <ActionMessageList
                                    scrollAreaRef={scrollAreaRef}
                                    onScroll={handleScroll}
                                    messages={contact?.Messages}
                                    contact={contact}
                                    // Pass notification props to message list
                                    notificationProps={{
                                        nextActionSuggestedAt: contact?.nextActionSuggestedAt,
                                        snoozedUntil: contact?.snoozedUntil,
                                        lastMessageSentAt:
                                            contact?.Messages && contact.Messages.length > 0
                                                ? new Date(contact.Messages[contact.Messages.length - 1].sentOn)
                                                : null,
                                        metadataLastUpdatedAt: contact?.metadataLastUpdatedAt
                                            ? new Date(contact.metadataLastUpdatedAt)
                                            : null,
                                        onSchedule: handleScheduleAction,
                                        onRefreshData: () => refetch(),
                                        messageJustSent,
                                    }}
                                />
                            ) : (
                                <div className="flex flex-col h-full justify-between items-center">
                                    <div className="flex flex-col h-full">
                                        <div className="flex flex-col items-center justify-center gap-3 p-10">
                                            <ContactAvatar
                                                avatarUrl={contact?.linkedinProfileImgUrl}
                                                fullName={contact?.fullName}
                                                size="xl"
                                            />
                                            {contact?.fullName ? (
                                                <span className="text-md font-medium">{contact.fullName}</span>
                                            ) : (
                                                <Skeleton className="h-6 w-48 bg-background-brand-primary-subtle" />
                                            )}
                                            <div className="flex flex-row bg-background-tertiary rounded-xl p-2 items-center justify-center text-sm text-center">
                                                <span>
                                                    Adjust the message as needed using the
                                                    <div className=" mx-1 inline-block">
                                                        <SparklesAI height={'14px'} />
                                                    </div>
                                                    button and get the conversation going.
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Suspense>

                        <div className="flex bottom-auto left-auto right-auto mb-14 md:mb-0 md:px-4 md:pb-4 md:w-full md:min-w-full bg-background flex-shrink-0">
                            <Suspense
                                fallback={
                                    <Skeleton className="h-[200px] w-full border-4 border-background-brand-primary-subtle">
                                        <Skeleton className="m-5 w-[80%] h-2 bg-background-brand-primary-subtle" />
                                    </Skeleton>
                                }
                            >
                                <MessageComposerV2 size={messageComposerSize} intercomAdjustedSendButton />
                            </Suspense>
                        </div>
                    </MessageComposerProviderV2>
                </div>
            </div>
            <ContactDetailsSidebar />
            <SnoozeDialogV2Component />
        </div>
    );
};
